import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { QuestionGroupsTab } from "@/components/questions/QuestionGroupsTab";
import { QuestionsTab } from "@/components/questions/QuestionsTab";
import type { Tables } from "@/integrations/supabase/types";
import { useAuth } from "@/hooks/useAuth";
import { USER_ROLES } from "@/types/enums";

type QuestionGroup = Tables<"question_groups"> & {
  questions?: { count: number }[];
};
type Question = Tables<"questions"> & {
  question_groups?: { name: string } | null;
};
type JobPosition = { id: string; role_name: string };

export default function QuestionsPage() {
  const [activeTab, setActiveTab] = useState("groups");

  const { profile, loading: authLoading, profileLoading } = useAuth();
  const authResolved = !authLoading && !profileLoading;
  const isPanelist = profile?.role === USER_ROLES.PANELIST;

  const { data: assignedJobPositionIds = [] } = useQuery({
    queryKey: ["panelist-job-positions", profile?.id],
    queryFn: async () => {
      if (!profile?.id) return [];

      const { data, error } = await supabase
        .from("job_positions")
        .select("id")
        .contains("interviewer", [profile.id])
        .eq("is_active", true);

      if (error) throw error;
      return (data || []).map((position) => position.id).sort();
    },
    enabled: authResolved && isPanelist && !!profile?.id,
  });

  const {
    data: questionGroups = [],
    isLoading: groupsLoading,
    refetch: refetchGroups,
  } = useQuery({
    queryKey: ["question-groups", isPanelist, assignedJobPositionIds],
    enabled: authResolved,
    queryFn: async () => {
      if (isPanelist && assignedJobPositionIds.length === 0) {
        return [] as QuestionGroup[];
      }

      let query = supabase
        .from("question_groups")
        .select("*, questions(count)")
        .order("sort_order", { ascending: true });

      if (isPanelist) {
        query = query.overlaps("job_position_ids", assignedJobPositionIds);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as QuestionGroup[];
    },
  });

  const allowedGroupIds = useMemo(
    () => questionGroups.map((group) => group.id),
    [questionGroups]
  );

  const {
    data: questions = [],
    isLoading: questionsLoading,
    refetch: refetchQuestions,
  } = useQuery({
    queryKey: ["questions", isPanelist, allowedGroupIds],
    enabled: authResolved,
    queryFn: async () => {
      if (isPanelist && allowedGroupIds.length === 0) {
        return [] as Question[];
      }

      let query = supabase
        .from("questions")
        .select(
          `
          *,
          question_groups:group_id (name)
        `
        )
        .order("created_at", { ascending: false });

      if (isPanelist) {
        query = query.in("group_id", allowedGroupIds);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as Question[];
    },
  });

  const { data: jobPositions = [] } = useQuery({
    queryKey: ["job-positions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("job_positions")
        .select("id, role_name")
        .eq("is_active", true);

      if (error) throw error;
      return data as JobPosition[];
    },
  });

  const handleRefetch = () => {
    refetchGroups();
    refetchQuestions();
  };

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Question Management</h1>
        <p className="text-muted-foreground mt-2">
          Manage question groups and individual questions for assessments
        </p>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="space-y-6"
      >
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="groups">Question Groups</TabsTrigger>
          <TabsTrigger value="questions">Questions</TabsTrigger>
        </TabsList>

        <TabsContent value="groups" className="space-y-6">
          <QuestionGroupsTab
            questionGroups={questionGroups}
            questions={questions}
            loading={!authResolved || groupsLoading}
            onRefetch={handleRefetch}
            canDelete={!isPanelist}
          />
        </TabsContent>

        <TabsContent value="questions" className="space-y-6">
          <QuestionsTab
            questions={questions}
            questionGroups={questionGroups}
            jobPositions={jobPositions}
            loading={!authResolved || questionsLoading}
            onRefetch={handleRefetch}
            canDelete={!isPanelist}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
